import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { CheckCircle2, Inbox, Loader2, LogOut, RefreshCw, Search } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { LogoMark } from "@/components/Logo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICE_LABELS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "hm_admin_token";

const STATUS_TABS = [
    { key: "all", label: "All" },
    { key: "new", label: "New" },
    { key: "reviewed", label: "Reviewed" },
];

const Admin = () => {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
    const [loginForm, setLoginForm] = useState({ email: "", password: "" });
    const [loginError, setLoginError] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);

    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState("all");
    const [service, setService] = useState("all");
    const [query, setQuery] = useState("");

    useEffect(() => {
        document.title = "Admin Inbox — Hallomedics";
    }, []);

    const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const params = {};
            if (status !== "all") params.status = status;
            if (service !== "all") params.service = service;
            if (query.trim()) params.q = query.trim();
            const { data } = await axios.get(`${API}/enquiries`, { ...authHeaders(), params });
            setEnquiries(data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem(TOKEN_KEY);
                setToken("");
                toast.error("Session expired — please sign in again.");
            } else {
                toast.error("Could not load enquiries.");
            }
        } finally {
            setLoading(false);
        }
    }, [token, status, service, query, authHeaders]);

    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
    }, [load]);

    const login = async (e) => {
        e.preventDefault();
        setLoginError("");
        setLoggingIn(true);
        try {
            const { data } = await axios.post(`${API}/auth/login`, loginForm);
            localStorage.setItem(TOKEN_KEY, data.token);
            setToken(data.token);
            toast.success(`Welcome back, ${data.user.name}.`);
        } catch (err) {
            const detail = err.response?.data?.detail;
            setLoginError(typeof detail === "string" ? detail : "Sign-in failed. Check your credentials.");
        } finally {
            setLoggingIn(false);
        }
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setEnquiries([]);
    };

    const setStatusFor = async (id, next) => {
        try {
            await axios.patch(`${API}/enquiries/${id}`, { status: next }, authHeaders());
            setEnquiries((list) => list.map((enq) => (enq.id === id ? { ...enq, status: next } : enq)));
            toast.success(next === "reviewed" ? "Marked as reviewed." : "Moved back to new.");
        } catch {
            toast.error("Could not update status.");
        }
    };

    if (!token) {
        return (
            <div className="flex min-h-screen items-center justify-center px-5 pt-16" data-testid="admin-login-page">
                <Reveal className="w-full max-w-md">
                    <form onSubmit={login} className="rounded-lg border border-border bg-card p-8 sm:p-10" data-testid="admin-login-form">
                        <LogoMark className="h-11 w-11" />
                        <h1 className="mt-6 font-heading text-2xl font-bold tracking-tight">Admin inbox</h1>
                        <p className="mt-1 text-sm text-muted-foreground">Sign in to read project enquiries.</p>
                        <div className="mt-7 space-y-5">
                            <div className="space-y-2">
                                <Label htmlFor="admin-email">Email</Label>
                                <Input id="admin-email" data-testid="admin-email-input" type="email" required value={loginForm.email} onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))} placeholder="admin@hallomedics.com" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="admin-password">Password</Label>
                                <Input id="admin-password" data-testid="admin-password-input" type="password" required value={loginForm.password} onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))} placeholder="••••••••••••" />
                            </div>
                            {loginError && (
                                <p className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert" data-testid="admin-login-error">
                                    {loginError}
                                </p>
                            )}
                            <Button type="submit" disabled={loggingIn} data-testid="admin-login-submit" className="h-11 w-full rounded-full text-sm font-semibold">
                                {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                            </Button>
                        </div>
                    </form>
                </Reveal>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-7xl px-5 pb-24 pt-28 sm:px-8 lg:pt-36" data-testid="admin-inbox-page">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <p className="eyebrow">Internal</p>
                    <h1 className="mt-2 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="admin-inbox-heading">
                        Enquiry inbox
                    </h1>
                </div>
                <div className="flex items-center gap-2.5">
                    <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="admin-refresh-button" className="rounded-full">
                        <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                    <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout-button" className="rounded-full">
                        <LogOut className="mr-2 h-4 w-4" />
                        Sign out
                    </Button>
                </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="admin-filters">
                <div className="flex gap-2">
                    {STATUS_TABS.map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setStatus(t.key)}
                            data-testid={`admin-status-${t.key}`}
                            className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                                status === t.key
                                    ? "border-primary bg-primary text-primary-foreground"
                                    : "border-border bg-card text-muted-foreground hover:text-primary dark:hover:text-teal"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <Select value={service} onValueChange={setService}>
                    <SelectTrigger className="w-52" data-testid="admin-service-filter" aria-label="Filter by service">
                        <SelectValue placeholder="All services" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all" data-testid="admin-service-all">All services</SelectItem>
                        {Object.entries(SERVICE_LABELS).map(([key, label]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <div className="relative min-w-56 flex-1">
                    <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email or organization…" className="pl-10" data-testid="admin-search-input" />
                </div>
            </div>

            <div className="mt-8 space-y-4" data-testid="admin-enquiry-list">
                {loading && enquiries.length === 0 ? (
                    <div className="flex items-center justify-center rounded-lg border border-border bg-card py-20">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : enquiries.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card py-20 text-center" data-testid="admin-empty-state">
                        <Inbox className="h-8 w-8 text-muted-foreground" />
                        <p className="mt-4 font-heading text-lg font-semibold">No enquiries yet</p>
                        <p className="mt-1 text-sm text-muted-foreground">New project enquiries from the contact form will appear here.</p>
                    </div>
                ) : (
                    enquiries.map((enq) => (
                        <article key={enq.id} className="rounded-lg border border-border bg-card p-6" data-testid={`enquiry-card-${enq.id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <div className="flex flex-wrap items-center gap-2.5">
                                        <h2 className="font-heading text-lg font-semibold tracking-tight">{enq.name}</h2>
                                        <Badge variant={enq.status === "new" ? "default" : "secondary"} data-testid={`enquiry-status-${enq.id}`} className={enq.status === "new" ? "bg-terracotta text-white hover:bg-terracotta" : ""}>
                                            {enq.status === "new" ? "New" : "Reviewed"}
                                        </Badge>
                                        <Badge variant="outline">{SERVICE_LABELS[enq.service] || enq.service}</Badge>
                                    </div>
                                    <p className="mt-1 text-sm text-muted-foreground">
                                        <a href={`mailto:${enq.email}`} className="font-medium text-foreground hover:text-primary dark:hover:text-teal">{enq.email}</a>
                                        {enq.organization ? ` · ${enq.organization}` : ""}
                                        {" · "}
                                        {format(new Date(enq.created_at), "dd MMM yyyy, HH:mm")}
                                    </p>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="rounded-full"
                                    data-testid={`enquiry-toggle-${enq.id}`}
                                    onClick={() => setStatusFor(enq.id, enq.status === "new" ? "reviewed" : "new")}
                                >
                                    <CheckCircle2 className="mr-2 h-4 w-4" />
                                    {enq.status === "new" ? "Mark reviewed" : "Move to new"}
                                </Button>
                            </div>
                            <p className="mt-4 whitespace-pre-wrap border-t border-border pt-4 text-sm leading-relaxed text-muted-foreground">{enq.message}</p>
                        </article>
                    ))
                )}
            </div>
        </div>
    );
};

export default Admin;
